/**
 * Unified API Client with automatic token refresh
 *
 * Features:
 * - Automatic token refresh on 401 errors
 * - Consistent error handling
 * - Request queuing during token refresh
 * - Type-safe responses
 */

import { API_CONFIG } from '../config/api';
import { authService } from './authService';
import {
    appendPaymentSupportContact,
    formatPaymentErrorMessage,
    isStripePaymentEndpoint,
    logPaymentFailure,
} from '../utils/paymentErrors';

// Standard API response format from backend
export interface ApiResponse<T = any> {
    success: boolean;
    data?: T;
    message?: string;
    error?: {
        message: string;
        code?: string;
    };
}

// Request options
interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: any;
    headers?: Record<string, string>;
    skipAuth?: boolean; // Skip auth header (for login/register)
}

// Token refresh state
let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];
const REFRESH_TIMEOUT_MS = 10000; // 10 second timeout for token refresh

/**
 * Subscribe to token refresh completion
 */
function subscribeTokenRefresh(callback: (token: string | null) => void) {
    refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers that refresh is complete (or failed)
 */
function onRefreshed(token: string | null) {
    const subscribers = [...refreshSubscribers];
    refreshSubscribers = [];
    subscribers.forEach(callback => callback(token));
}

/**
 * Attempt to refresh the access token
 */
async function refreshAccessToken(): Promise<string | null> {
    try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            return null;
        }

        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REFRESH_TOKEN}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
            // Refresh token is invalid or expired
            return null;
        }

        const data = await response.json();
        if (data.success && data.data?.accessToken) {
            const newAccessToken = data.data.accessToken;
            const newRefreshToken = data.data.refreshToken || refreshToken;

            // Update stored tokens
            localStorage.setItem('accessToken', newAccessToken);
            localStorage.setItem('refreshToken', newRefreshToken);

            return newAccessToken;
        }

        return null;
    } catch (error) {
        console.error('Token refresh failed:', error);
        return null;
    }
}

/**
 * Handle 401 error with token refresh
 * Includes timeout protection and proper cleanup
 */
async function handleUnauthorized(): Promise<string | null> {
    if (isRefreshing) {
        // Wait for the ongoing refresh to complete with timeout
        return new Promise<string | null>((resolve) => {
            const timeoutId = setTimeout(() => {
                // Remove this subscriber and resolve with null on timeout
                const index = refreshSubscribers.indexOf(callback);
                if (index > -1) {
                    refreshSubscribers.splice(index, 1);
                }
                resolve(null);
            }, REFRESH_TIMEOUT_MS);

            const callback = (token: string | null) => {
                clearTimeout(timeoutId);
                resolve(token);
            };

            subscribeTokenRefresh(callback);
        });
    }

    isRefreshing = true;

    try {
        const newToken = await refreshAccessToken();
        if (newToken) {
            onRefreshed(newToken);
            return newToken;
        } else {
            // Refresh failed - notify subscribers and logout
            onRefreshed(null);
            await authService.logout();
            window.location.href = '/login';
            return null;
        }
    } catch (error) {
        // On error, notify subscribers and cleanup
        onRefreshed(null);
        throw error;
    } finally {
        isRefreshing = false;
    }
}

/**
 * Parse JSON response text, returning a friendly error when HTML is returned
 * (e.g. Vercel 404 page after a stale deploy — causes "Unexpected token '<'").
 */
function parseResponseJson(text: string, endpoint: string): any {
    if (!text) return { success: true };
    const trimmed = text.trimStart();
    if (trimmed.startsWith('<')) {
        throw new Error(
            `Server returned HTML instead of JSON for ${endpoint}. Try refreshing the page.`
        );
    }
    try {
        return JSON.parse(text);
    } catch (parseError) {
        throw new Error(
            `Invalid JSON response for ${endpoint}: ${parseError instanceof Error ? parseError.message : 'parse failed'}`
        );
    }
}

/**
 * Extract error message from API response
 */
function extractErrorMessage(data: any, defaultMessage: string, endpoint?: string): string {
    let message = defaultMessage;
    if (!data) {
        return message;
    }
    // { error: { message: "..." } }
    if (typeof data.error?.message === 'string') {
        message = data.error.message;
    } else if (typeof data.error === 'string') {
        // { error: "string" } — teams handler format
        message = data.error;
    } else if (typeof data.message === 'string') {
        message = data.message;
    }

    if (endpoint && isStripePaymentEndpoint(endpoint)) {
        return appendPaymentSupportContact(message);
    }

    return message;
}

/**
 * Make an API request with automatic token refresh
 */
export async function apiRequest<T = any>(
    endpoint: string,
    options: RequestOptions = {}
): Promise<ApiResponse<T>> {
    const { method = 'GET', body, headers = {}, skipAuth = false } = options;

    const token = authService.getAccessToken();

    const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...headers,
    };

    if (!skipAuth && token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
    }

    const url = endpoint.startsWith('http') ? endpoint : `${API_CONFIG.BASE_URL}${endpoint}`;

    try {
        const response = await fetch(url, {
            method,
            headers: requestHeaders,
            body: body ? JSON.stringify(body) : undefined,
        });

        // 5xx errors — never attempt token refresh, return error immediately
        if (response.status >= 500) {
            return {
                success: false,
                error: {
                    message: 'Server error (' + response.status + ')',
                    code: response.status.toString(),
                },
            };
        }

        // Handle 401 - attempt token refresh
        // Skip logout for non-critical endpoints — let caller handle the error
        if (response.status === 401 && !skipAuth && url.includes('/steps/pending')) {
            return {
                success: false,
                error: {
                    message: 'Unauthorized',
                    code: '401',
                },
            };
        }
        if (response.status === 401 && !skipAuth) {
            const newToken = await handleUnauthorized();
            if (newToken) {
                // Retry the request with the new token
                const retryResponse = await fetch(url, {
                    method,
                    headers: {
                        ...requestHeaders,
                        'Authorization': `Bearer ${newToken}`,
                    },
                    body: body ? JSON.stringify(body) : undefined,
                });

                const retryText = await retryResponse.text();
                const retryData = parseResponseJson(retryText, endpoint);
                if (!retryResponse.ok) {
                    const retryMessage = extractErrorMessage(retryData, 'Request failed', endpoint);
                    if (isStripePaymentEndpoint(endpoint)) {
                        logPaymentFailure({ flow: endpoint }, new Error(retryMessage));
                    }
                    return {
                        success: false,
                        error: {
                            message: retryMessage,
                        },
                    };
                }
                return retryData;
            }

            return {
                success: false,
                error: {
                    message: 'Session expired. Please log in again.',
                    code: 'SESSION_EXPIRED',
                },
            };
        }

        // Handle empty responses (e.g., 204 No Content from DELETE)
        const text = await response.text();
        const data = parseResponseJson(text, endpoint);

        if (!response.ok) {
            const errorMessage = extractErrorMessage(data, 'Request failed', endpoint);
            if (isStripePaymentEndpoint(endpoint)) {
                logPaymentFailure(
                    { flow: endpoint, metadata: { status: response.status } },
                    new Error(errorMessage)
                );
            }
            return {
                success: false,
                error: {
                    message: errorMessage,
                    code: response.status.toString(),
                },
            };
        }

        return data;
    } catch (error: any) {
        console.error(`API request failed: ${method} ${endpoint}`, error);
        const networkMessage = error.message || 'Network error. Please check your connection.';
        const userMessage = isStripePaymentEndpoint(endpoint)
            ? formatPaymentErrorMessage(networkMessage)
            : networkMessage;
        if (isStripePaymentEndpoint(endpoint)) {
            logPaymentFailure({ flow: endpoint }, error);
        }
        return {
            success: false,
            error: {
                message: userMessage,
                code: 'NETWORK_ERROR',
            },
        };
    }
}

// Convenience methods
export const api = {
    get: <T = any>(endpoint: string, options?: Omit<RequestOptions, 'method'>) =>
        apiRequest<T>(endpoint, { ...options, method: 'GET' }),

    post: <T = any>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>) =>
        apiRequest<T>(endpoint, { ...options, method: 'POST', body }),

    put: <T = any>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>) =>
        apiRequest<T>(endpoint, { ...options, method: 'PUT', body }),

    patch: <T = any>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>) =>
        apiRequest<T>(endpoint, { ...options, method: 'PATCH', body }),

    delete: <T = any>(endpoint: string, options?: Omit<RequestOptions, 'method'>) =>
        apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};
